// web/src/components/users/UserDetailsModal.jsx
import React, { useEffect, useState } from "react";
import "./UserDetailsModal.css";
import UserVerificationBadge from "./UserVerificationBadge";

export default function UserDetailsModal({
  user,
  onClose,
  onChangeVerification,
  onToggleBan,
}) {
  // ✅ Hooks SEMPRE no topo
  const [previewUrl, setPreviewUrl] = useState(null);

  // ✅ Fecha preview com ESC
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") setPreviewUrl(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!user) return null;

  // ============================================================
  // Helpers (Timestamp / string / Date)
  // ============================================================
  const toDate = (value) => {
    if (!value) return null;
    try {
      // Firestore Timestamp (seconds)
      if (value?.seconds) return new Date(value.seconds * 1000);

      // Firestore Timestamp (toDate)
      if (typeof value?.toDate === "function") return value.toDate();

      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      return d;
    } catch (_) {
      return null;
    }
  };

  const calcAge = (birth) => {
    const d = toDate(birth);
    if (!d) return "-";
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  };

  const toStringArray = (raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr.map((x) => String(x || "").trim()).filter(Boolean);
  };

  const isHttp = (url) => typeof url === "string" && url.startsWith("http");

  // ============================================================
  // Dados (compat mobile + legacy)
  // ============================================================
  const {
    id,
    name,
    email,
    plan,
    city,
    gender,

    // ✅ mobile: birthDate | web: birthdate
    birthDate,
    birthdate,

    bio,
    createdAt,
    lastLogin,
    verificationStatus,
    isBanned,

    lifestyleTags,
    values,
    preferences,
    dealbreakers,

    // fotos possíveis
    avatarUrls,
    albumPhotos,
    photos,
    album,
    photoUrl,
  } = user;

  const birth = birthDate ?? birthdate ?? null;

  const age = calcAge(birth);
  const createdDate = toDate(createdAt);
  const lastLoginDate = toDate(lastLogin);
  const birthDateObj = toDate(birth);

  // ============================================================
  // ✅ Lista final de fotos (SEM useMemo)
  // ============================================================
  const mergedPhotos = [
    ...toStringArray(avatarUrls),
    ...toStringArray(albumPhotos),
    ...toStringArray(photos),
    ...toStringArray(album),
  ].filter(isHttp);

  const photosList = Array.from(new Set(mergedPhotos)).slice(0, 12);

  // ✅ Foto principal topo: pega a 1ª do álbum; senão photoUrl
  const mainPhoto =
    (isHttp(photosList?.[0]) ? photosList[0] : null) ||
    (isHttp(photoUrl) ? photoUrl : null) ||
    null;

  function openPreview(url) {
    if (!isHttp(url)) return;
    setPreviewUrl(url);
  }

  function closePreview() {
    setPreviewUrl(null);
  }

  return (
    <div className="user-modal-overlay" onClick={onClose}>
      <div className="user-modal" onClick={(e) => e.stopPropagation()}>
        {/* FECHAR */}
        <button
          className="user-modal-close"
          onClick={onClose}
          aria-label="Fechar"
          type="button"
        >
          ✕
        </button>

        <h2 className="user-modal-title">Detalhes do usuário</h2>

        <div className="user-modal-content">
          {/* ESQUERDA */}
          <div className="user-modal-left">
            {/* ✅ Avatar com imagem quando existir */}
            <div
              className={`user-avatar ${mainPhoto ? "has-photo" : ""}`}
              onClick={() => mainPhoto && openPreview(mainPhoto)}
              role={mainPhoto ? "button" : undefined}
              tabIndex={mainPhoto ? 0 : undefined}
              onKeyDown={(e) => {
                if (mainPhoto && (e.key === "Enter" || e.key === " ")) {
                  openPreview(mainPhoto);
                }
              }}
              aria-label={mainPhoto ? "Abrir foto principal" : undefined}
            >
              {mainPhoto ? (
                <img
                  src={mainPhoto}
                  alt="Foto do usuário"
                  className="user-avatar-img"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span>{name?.charAt(0) || "?"}</span>
              )}
            </div>

            <h3 className="user-name">{name || "Sem nome"}</h3>
            <p className="user-email">{email || "Sem e-mail"}</p>

            <UserVerificationBadge status={verificationStatus} />

            <div className="user-info-box">
              <p>
                <strong>Plano:</strong> {plan || "free"}
              </p>
              <p>
                <strong>Status:</strong> {isBanned ? "Banido" : "Ativo"}
              </p>
            </div>

            {/* BOTÕES ADMIN */}
            <div className="user-actions">
              {verificationStatus === "pending" ? (
                <button
                  className="btn-approve"
                  onClick={() => onChangeVerification(id, "verified")}
                  type="button"
                >
                  Aprovar verificação
                </button>
              ) : (
                <button
                  className="btn-cancel"
                  onClick={() => onChangeVerification(id, "pending")}
                  type="button"
                >
                  Revogar verificação
                </button>
              )}

              <button
                className="btn-ban"
                onClick={() => onToggleBan(id)}
                type="button"
              >
                {isBanned ? "Reativar usuário" : "Banir usuário"}
              </button>
            </div>
          </div>

          {/* DIREITA */}
          <div className="user-modal-right">
            <p>
              <strong>Cidade:</strong> {city || "-"}
            </p>
            <p>
              <strong>Gênero:</strong> {gender || "-"}
            </p>
            <p>
              <strong>Idade:</strong> {age} anos
            </p>

            <p>
              <strong>Data de nascimento:</strong>{" "}
              {birthDateObj ? birthDateObj.toLocaleDateString() : "-"}
            </p>

            <p>
              <strong>Criado em:</strong>{" "}
              {createdDate ? createdDate.toLocaleDateString() : "-"}
            </p>

            <p>
              <strong>Último acesso:</strong>{" "}
              {lastLoginDate ? lastLoginDate.toLocaleDateString() : "-"}
            </p>

            {/* BIO */}
            <p className="user-bio">
              <strong>Bio:</strong>
              <br />
              {bio || "—"}
            </p>

            {/* Lifestyle */}
            <h4 className="section-title">🧘 Estilo de vida</h4>
            <div className="tag-list">
              {lifestyleTags?.length ? (
                lifestyleTags.map((t, i) => (
                  <span key={i} className="tag">
                    {t}
                  </span>
                ))
              ) : (
                <p className="no-data">Não informado</p>
              )}
            </div>

            {/* Valores essenciais */}
            <h4 className="section-title">💗 Valores essenciais</h4>
            <div className="tag-list">
              {values?.length ? (
                values.map((v, i) => (
                  <span key={i} className="tag">
                    {v}
                  </span>
                ))
              ) : (
                <p className="no-data">Não informado</p>
              )}
            </div>

            {/* Preferências */}
            <h4 className="section-title">🎯 Preferências</h4>
            <div className="tag-list">
              {preferences?.length ? (
                preferences.map((p, i) => (
                  <span key={i} className="tag">
                    {p}
                  </span>
                ))
              ) : (
                <p className="no-data">Não informado</p>
              )}
            </div>

            {/* Não negociáveis */}
            <h4 className="section-title">⛔ Não negociáveis</h4>
            <div className="tag-list">
              {dealbreakers?.length ? (
                dealbreakers.map((d, i) => (
                  <span key={i} className="tag">
                    {d}
                  </span>
                ))
              ) : (
                <p className="no-data">Não informado</p>
              )}
            </div>
          </div>
        </div>

        {/* FOTOS DO USUÁRIO */}
        <div className="user-photos-section">
          <h3>Fotos do usuário</h3>

          {photosList.length > 0 ? (
            <div className="photo-grid">
              {photosList.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  className="photo-thumb"
                  onClick={() => openPreview(p)}
                  aria-label={`Abrir foto ${i + 1}`}
                >
                  <img
                    src={p}
                    alt={`foto-${i + 1}`}
                    className="user-photo"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </button>
              ))}
            </div>
          ) : (
            <p className="no-photos">Nenhuma foto enviada</p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-close" onClick={onClose} type="button">
            Fechar
          </button>
        </div>

        {/* ✅ LIGHTBOX */}
        {previewUrl && (
          <div className="photo-preview-overlay" onClick={closePreview}>
            <div
              className="photo-preview-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="photo-preview-close"
                onClick={closePreview}
                aria-label="Fechar preview"
              >
                ✕
              </button>

              <img
                src={previewUrl}
                alt="Foto ampliada"
                className="photo-preview-img"
                loading="eager"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
